import { defineStore } from 'pinia'
import crypto from 'crypto-js'
import { Str } from 'laravel-js-str'
import api from '../api'
import axios from 'axios'
import { OFFICE_ID_PARAM } from '../helpers'
import { useSystemStore } from './system'
import { useNotificationStore } from './notifications'
import * as Sentry from '@sentry/vue'

let refreshingToken = null

export const useAuthStore = defineStore('auth', {
  state: () => ({
    authUser: null,
    authOffices: null,
    selectedOffice: null,
    network: null
  }),
  getters: {
    authInitialized() {
      return (
        this.authUser !== null &&
        this.authOffices !== null &&
        (this.authOffices.length === 0 || this.selectedOffice !== null)
      )
    },
    authAssignedCountries() {
      return this.authUser?.role === 'client admin'
        ? this.authOffices?.map((o) => o.country).filter((v, i, self) => self.indexOf(v) === i) || []
        : []
    }
  },
  actions: {
    async initAuth() {
      const systemStore = useSystemStore()
      await this.getAuthUser()
      await systemStore.bootSystem()
    },
    async getAuthUser() {
      const systemStore = useSystemStore()
      const response = await api.get('v3/users/me')
      this.authUser = response.data

      Sentry.setUser({
        id: this.authUser.id,
        email: this.authUser.email,
        username: this.authUser.name
      })

      if (systemStore.$state.hub.gtm_tag) {
        window.dataLayer = []
        window.dataLayer.push({ user_id: this.authUser.id })
        window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        if (document.getElementById('gtm-script')) return
        const script = document.createElement('script')
        script.id = 'gtm'
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${systemStore.$state.hub.gtm_tag}`
        document.head.appendChild(script)
      } else {
        window.dataLayer = []
      }

      systemStore.toggleDarkMode(response.data.dark_mode)
      systemStore.toggleUiAnimations(response.data.reduced_motion)
    },
    async initLogin(direct = false) {
      // await revokeToken()

      localStorage.state = Str.random(40)
      localStorage.codeVerifier = Str.random(128)
      localStorage.intended =
        window.location.pathname !== '/oauth/login'
          ? `${window.location.pathname}${window.location.search}`
          : window.location.search

      const host = import.meta.env.VITE_API_HOST
      const systemStore = useSystemStore()

      const query = new URLSearchParams({
        client_id: systemStore.hub.ecosystem_app.oauth_client_id,
        redirect_uri: `${window.location.protocol}//${window.location.host}/oauth/login`,
        response_type: 'code',
        scope: 'global',
        state: localStorage.state,
        code_challenge: crypto
          .SHA256(localStorage.codeVerifier)
          .toString(crypto.enc.Base64)
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=/g, ''),
        code_challenge_method: 'S256'
      })

      if (direct) {
        window.location.href = `${host}/oauth/authorize?${query}`
        return
      }

      const query2 = new URLSearchParams({
        r: `/oauth/authorize?${query}`
      })

      window.location.href = `${host}/login?${query2}`
    },
    async getToken(state, code) {
      const localState = localStorage.state
      const codeVerifier = localStorage.codeVerifier
      localStorage.removeItem('state')
      localStorage.removeItem('codeVerifier')
      if (localState !== state) {
        return Promise.reject('Unexpected state')
      }

      const host = import.meta.env.VITE_API_HOST
      const systemStore = useSystemStore()

      const response = await api.post(`${host}/oauth/token`, {
        grant_type: 'authorization_code',
        client_id: systemStore.hub.ecosystem_app.oauth_client_id,
        redirect_uri: `${window.location.protocol}//${window.location.host}/oauth/login`,
        code_verifier: codeVerifier,
        code: code
      })

      localStorage.accessToken = response.data.access_token
      localStorage.refreshToken = response.data.refresh_token

      return Promise.resolve(response)
    },
    // Swaps the refresh token for a new access token without leaving the page, for embeds such as the Competitor
    // Analysis viewer that hold the member's token and ask for another when it expires. Concurrent callers share
    // one request, because a refresh token can only be used once. Falls back to signing in again if it is refused.
    refreshAccessToken() {
      if (!refreshingToken) {
        refreshingToken = (async () => {
          try {
            const systemStore = useSystemStore()
            // Plain axios: a refused refresh goes straight to sign-in rather than the hub's error page
            const response = await axios.post(`${import.meta.env.VITE_API_HOST}/oauth/token`, {
              grant_type: 'refresh_token',
              refresh_token: localStorage.refreshToken,
              client_id: systemStore.hub.ecosystem_app.oauth_client_id,
              scope: 'global'
            })

            localStorage.accessToken = response.data.access_token
            localStorage.refreshToken = response.data.refresh_token

            return response.data.access_token
          } catch (e) {
            await this.initLogin()

            throw e
          } finally {
            refreshingToken = null
          }
        })()
      }

      return refreshingToken
    },
    logOut() {
      localStorage.clear()
      localStorage.office_id = this.selectedOffice?.id
      this.initLogin()
    },
    async getOffices() {
      const systemStore = useSystemStore()
      const response = await api.get(`/v3/members-hubs/${systemStore.hub.id}/offices`)
      this.authOffices = response.data

      // An inbound deep link may ask for a specific office (e.g. a LeadPro "More Info"
      // link). Only honour it when the user actually has access to that office,
      // otherwise fall back to the office they last used.
      const requestedOfficeId = new URLSearchParams(window.location.search).get(OFFICE_ID_PARAM)
      const canAccessRequested =
        requestedOfficeId !== null && this.authOffices.some((o) => o.id === parseInt(requestedOfficeId))

      await this.switchOffice(canAccessRequested ? requestedOfficeId : localStorage.office_id)
    },
    async switchOffice(id) {
      if (this.authOffices.length === 0) {
        return
      }
      let office = this.authOffices.find((o) => o.id === parseInt(id))
      if (!office) {
        if (this.selectedOffice) {
          return
        }
        office = this.authOffices[0]
      }

      const response = await api.get(`v3/offices/${office.id}`, {
        params: {
          with_referral_system_id: true
        }
      })
      this.selectedOffice = response.data
      localStorage.office_id = this.selectedOffice.id

      const networkResponse = await api.get(`v3/networks/${this.selectedOffice.network_id}`)
      this.network = networkResponse.data

      Sentry.setTag('office_id', this.selectedOffice.id)
      Sentry.setTag('office_name', this.selectedOffice.name)

      const systemStore = useSystemStore()
      systemStore.getCallToActions()

      const notificationStore = useNotificationStore()
      await notificationStore.getAlerts()
    }
  }
})
